import { z } from 'zod';

/**
 * Utility function to validate a single item and handle success or failure logging
 *
 * @param schema The Zod schema to validate the item against.
 * @param item The item to validate.
 * @param index The index of the item in the array (optional).
 * @returns The validated item if successful, otherwise null.
 */
export function validateAndLog<T extends z.ZodTypeAny>(
    schema: T,
    item: unknown,
    index?: number
): z.infer<T> | null {
    const result = schema.safeParse(item);
    if (result.success) {
        return result.data; // Return valid data
    } else {
        // Log errors if validation fails
        const indexMessage = index !== undefined ? ` at index ${index}` : '';
        console.error(
            `Partial validation failed for item${indexMessage}, item skipped.`,
            `Schema: ${schema.description}`,
            'Item:',
            item,
            'Errors:',
            result.error.errors
        );
        return null;
    }
}

/**
 * Validates items of any array returned by Pimcore GraphQL API using a given schema and filters
 * out invalid items.
 *
 * @param schema The schema to validate the items.
 * @param items The items to transform and filter.
 * @returns The transformed and filtered items.
 */
export function transformAndFilterValidItems<T extends z.ZodTypeAny>(
    schema: T,
    items: Array<unknown> | null | undefined
): z.infer<T>[] {
    return (
        items
            ?.map((item, index) => validateAndLog(schema, item, index))
            .filter((item): item is z.infer<T> => item !== null) ?? []
    );
}

/**
 * Splits a plaintext input into a list of cleaned lines.
 * - Splits by newlines (handles both LF and CRLF).
 * - Trims whitespace from each line.
 * - Removes common list markers (e.g., '-', '*', '1.', etc.) from the start of lines.
 * - Filters out empty lines.
 *
 * @param input The plaintext input to split.
 * @returns An array of cleaned lines.
 */
export function splitPlaintextToList(input: string | null | undefined): string[] {
    if (!input) return [];
    return input
        .split(/\r?\n+/)
        .map((line) => line.trim())
        .map((line) => line.replace(/^(?:[-*+•–—]\s+|\d+[.)]\s+)/, ''))
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
}

/**
 * Normalizes a multiline string into a single line by:
 * - Replacing all newline characters with spaces.
 * - Collapsing multiple consecutive whitespace characters into a single space.
 * - Trimming leading and trailing whitespace.
 *
 * @param input The multiline string to normalize.
 * @returns The normalized single-line string.
 */
export function normalizeSingleLine(input: string): string {
    return input
        .replace(/[\r\n]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * Parses a plaintext body into an array of paragraphs.
 * - Splits by newlines (handles both LF and CRLF).
 * - Trims whitespace from each line.
 * - If there are no empty lines, each non-empty line is treated as a separate paragraph.
 * - If there are empty lines, groups of non-empty lines separated by empty lines are treated as paragraphs,
 *   with lines within a group concatenated with spaces.
 *
 * @param input The plaintext body to parse.
 * @returns An array of paragraphs.
 */
export function parseBodyToParagraphs(input: string): string[] {
    if (!input) return [];
    const lines = input.split(/\r?\n/);
    // Detect presence of empty lines
    const hasEmpty = lines.some((l) => l.trim() === '');
    if (!hasEmpty) {
        // Each non-empty line is a paragraph
        return lines.map((l) => l.trim()).filter((l) => l.length > 0);
    }
    // Group by empty lines. Concatenate non-empty lines within a group separated by spaces.
    const paragraphs: string[] = [];
    let current: string[] = [];
    for (const raw of lines) {
        const l = raw.trim();
        if (l === '') {
            if (current.length > 0) {
                paragraphs.push(current.join(' '));
                current = [];
            }
        } else {
            current.push(l);
        }
    }
    if (current.length > 0) paragraphs.push(current.join(' '));
    return paragraphs;
}
